import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Grid, Card, CardContent, Stack,
  TextField, Switch, FormControlLabel, Button, Chip, Divider, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  FormControl, InputLabel, Snackbar, InputAdornment, LinearProgress,
} from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SecurityIcon from '@mui/icons-material/Security';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link, useLocation, useHistory } from 'react-router-dom';
import ExtensionIcon from '@mui/icons-material/Extension';
import TimerIcon from '@mui/icons-material/Timer';
import BlockIcon from '@mui/icons-material/Block';
import WorkingTypesTab from '../components/settings/WorkingTypesTab';
import CircularProgress from '@mui/material/CircularProgress';
import apiService from '../services/apiServices';
import PageHeader from '../components/common/PageHeader';
import ShiftPlanning from '../components/company/ShiftPlanning';
import { useModuleSettings } from '../hooks/useModuleSettings';
import { getAuthToken } from '../services/authSession';
import useConfirm from '../hooks/useConfirm';

// ─── helpers ────────────────────────────────────────────────────────────────
const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const DAY_ALB = { Monday:'E Hënë', Tuesday:'E Martë', Wednesday:'E Mërkurë', Thursday:'E Enjte', Friday:'E Premte', Saturday:'E Shtunë', Sunday:'E Diel' };

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2.5 }}>{children}</Box>;
}

function SectionCard({ title, icon, children, action }) {
  return (
    <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
      <Box sx={{ p: '10px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {icon}
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
        </Stack>
        {action}
      </Box>
      <CardContent sx={{ p: '16px !important' }}>{children}</CardContent>
    </Card>
  );
}

// ─── Tab 1: Fiskal ──────────────────────────────────────────────────────────
function FiskalTab() {
  const [policies, setPolicies] = useState([]);
  useEffect(() => {
    apiService().get('fiscalPoliciesExt').then(r => { if (r.success) setPolicies(r.data); });
  }, []);

  const active = policies.find(p => p.Active);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Konfigurimet fiskale menaxhohen te <strong>Politikat Fiskale</strong>. Këtu shihni një rezyme dhe lidhje të shpejtë.
      </Alert>

      {active && (
        <SectionCard title="Politika aktive" icon={<AccountBalanceIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
          action={
            <Button size="small" component={Link} to="/fiscal" endIcon={<ArrowForwardIcon />} sx={{ textTransform: 'none' }}>
              Menaxho
            </Button>
          }
        >
          <Grid container spacing={2}>
            {[
              { label: 'Pensioni (punëtor)', value: `${active.PensionEmployee ?? 5}%`, color: '#3E8FA3', bg: '#EEF2FF' },
              { label: 'Pensioni (punëdhënës)', value: `${active.PensionEmployer ?? 5}%`, color: '#3E8FA3', bg: '#ECFDF5' },
              { label: 'Paga minimale', value: `${active.MinimumWage ?? 264} €`, color: '#D97706', bg: '#FFFBEB' },
              { label: 'Orë shtesë maks/javë', value: `${active.MaxOvertimeHoursWeek ?? active.MaxOvertimeHours ?? 8}h/javë`, color: '#DC2626', bg: '#FEF2F2' },
            ].map(s => (
              <Grid item xs={6} sm={3} key={s.label}>
                <Box sx={{ bgcolor: s.bg, borderRadius: 1.5, p: 1.5, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: s.color, fontWeight: 700, display: 'block' }}>{s.label}</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            <PercentIcon sx={{ fontSize: 12, mr: 0.5 }} />Shkallët tatimore (Kosovë)
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {[['0–250€','0%'],['250–450€','8%'],['>450€','10%']].map(([r, t]) => (
              <Chip key={r} label={`${r} → ${t}`} size="small" variant="outlined" color="info" />
            ))}
          </Stack>
        </SectionCard>
      )}

      <SectionCard title="Të gjitha politikat" icon={<AccountBalanceIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        action={
          <Button size="small" variant="contained" component={Link} to="/fiscal/addfiscal" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}>
            Politikë e re
          </Button>
        }
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700 }}>Emri</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Vlefshme nga</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Deri</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Pension %</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Statusi</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {policies.map(p => (
              <TableRow key={p.Id} hover>
                <TableCell>{p.Name}</TableCell>
                <TableCell>{p.ValidFrom || '—'}</TableCell>
                <TableCell>{p.ValidTo || 'Pa limit'}</TableCell>
                <TableCell>{p.PensionEmployee ?? 5}% / {p.PensionEmployer ?? 5}%</TableCell>
                <TableCell>
                  <Chip size="small" label={p.Active ? 'Aktive' : 'Joaktive'} color={p.Active ? 'success' : 'default'} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" component={Link} to={`/fiscal/editfiscal/${p.Id}`}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>
    </Box>
  );
}

// ─── Tab 2: Orari ───────────────────────────────────────────────────────────
export function OrariTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState('');

  useEffect(() => {
    apiService().get('workScheduleSettings').then(r => {
      if (r.success && r.data?.length) setCfg(r.data[0]);
      else setCfg({ Id:1, WeeklyHours:40, DailyHours:8, WorkDays:['Monday','Tuesday','Wednesday','Thursday','Friday'], OvertimeMinRate:125, OvertimeRequiresApproval:true, OvertimeDailyLimitHours:4, OvertimeYearlyLimitHours:200, FlexibleHours:false, BreakMinutes:30 });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    await apiService().put('workScheduleSettings', { ...cfg, UpdatedAt: new Date().toISOString() });
    setSaving(false);
    setSnack('Konfigurimi u ruajt me sukses!');
  };

  const toggleDay = (day) => {
    setCfg(prev => ({
      ...prev,
      WorkDays: prev.WorkDays.includes(day) ? prev.WorkDays.filter(d => d !== day) : [...prev.WorkDays, day],
    }));
  };

  if (!cfg) return <LinearProgress />;

  return (
    <Box>
      <SectionCard title="Orari standard i punës" icon={<ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
        action={<Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving} sx={{ textTransform: 'none' }}>Ruaj</Button>}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Orë javore" type="number" size="small" fullWidth value={cfg.WeeklyHours}
              onChange={e => setCfg(p => ({ ...p, WeeklyHours: +e.target.value }))}
              InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Orë ditore" type="number" size="small" fullWidth value={cfg.DailyHours}
              onChange={e => setCfg(p => ({ ...p, DailyHours: +e.target.value }))}
              InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Pushim (minuta)" type="number" size="small" fullWidth value={cfg.BreakMinutes}
              onChange={e => setCfg(p => ({ ...p, BreakMinutes: +e.target.value }))}
              InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel sx={{ mt: 0.5 }} control={
              <Switch checked={cfg.FlexibleHours} onChange={e => setCfg(p => ({ ...p, FlexibleHours: e.target.checked }))} />
            } label="Orar fleksibël" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>Ditët e punës</Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap">
              {DAYS.map(day => (
                <Chip key={day} label={DAY_ALB[day]} size="small" clickable
                  color={cfg.WorkDays.includes(day) ? 'primary' : 'default'}
                  variant={cfg.WorkDays.includes(day) ? 'filled' : 'outlined'}
                  onClick={() => toggleDay(day)} />
              ))}
            </Stack>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Orët shtesë (Overtime)" icon={<ScheduleIcon sx={{ fontSize: 18, color: 'warning.main' }} />}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Norma minimale" type="number" size="small" fullWidth value={cfg.OvertimeMinRate}
              onChange={e => setCfg(p => ({ ...p, OvertimeMinRate: +e.target.value }))}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              helperText="Paga bazë × norma" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Limit ditor" type="number" size="small" fullWidth value={cfg.OvertimeDailyLimitHours}
              onChange={e => setCfg(p => ({ ...p, OvertimeDailyLimitHours: +e.target.value }))}
              InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Limit vjetor" type="number" size="small" fullWidth value={cfg.OvertimeYearlyLimitHours}
              onChange={e => setCfg(p => ({ ...p, OvertimeYearlyLimitHours: +e.target.value }))}
              InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel sx={{ mt: 0.5 }} control={
              <Switch checked={cfg.OvertimeRequiresApproval} onChange={e => setCfg(p => ({ ...p, OvertimeRequiresApproval: e.target.checked }))} />
            } label="Kërkon aprovim" />
          </Grid>
        </Grid>
      </SectionCard>

      <ShiftPlanning />


      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─── Tab 3: Pushimet ────────────────────────────────────────────────────────
export function PushimetTab() {
  const [types, setTypes] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [snack, setSnack] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    apiService().get('leaveTypes').then(r => { if (r.success) setTypes(r.data); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    if (!editItem) return;
    await apiService().put('leaveTypes', editItem);
    setEditItem(null);
    load();
    setSnack('Lloji i pushimit u ruajt.');
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await apiService().delete('leaveTypes', deleteId);
    setDeleteId(null);
    load();
    setSnack('Lloji u fshi.');
  };

  const addNew = () => setEditItem({ Id: 0, Code: '', Name: '', IsPaid: true, MaxDaysYear: 20, CarryOverDays: 0, RequiresCert: false, LpReference: '', IsSystem: false });

  return (
    <Box>
      <SectionCard title="Llojet e pushimeve" icon={<BeachAccessIcon sx={{ fontSize: 18, color: '#3E8FA3' }} />}
        action={<Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addNew} sx={{ textTransform: 'none' }}>Shto lloj</Button>}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              {['Kodi','Emri','I paguar','Maks ditë/vit','Barten','Kërkon cert.','Ref. ligjore',''].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map(t => (
              <TableRow hover key={t.Id}>
                <TableCell><Chip label={t.Code} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} /></TableCell>
                <TableCell><Typography variant="body2" fontWeight={600}>{t.Name}</Typography></TableCell>
                <TableCell>
                  <Chip size="small" label={t.IsPaid ? 'Po' : 'Jo'} color={t.IsPaid ? 'success' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell>{t.MaxDaysYear === 365 ? 'Pa limit' : `${t.MaxDaysYear}d`}</TableCell>
                <TableCell>{t.CarryOverDays > 0 ? `${t.CarryOverDays}d` : '—'}</TableCell>
                <TableCell>
                  {t.RequiresCert
                    ? <CheckCircleIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{t.LpReference || '—'}</Typography></TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.25}>
                    <IconButton size="small" onClick={() => setEditItem({ ...t })}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                    {!t.IsSystem && (
                      <IconButton size="small" color="error" onClick={() => setDeleteId(t.Id)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem?.Id === 0 ? 'Shto lloj pushimi' : 'Edito llojin e pushimit'}</DialogTitle>
        <DialogContent>
          {editItem && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Kodi (unik)" value={editItem.Code}
                  onChange={e => setEditItem(p => ({ ...p, Code: e.target.value }))}
                  disabled={editItem.IsSystem} helperText="p.sh. annual, sick" />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Emri" value={editItem.Name}
                  onChange={e => setEditItem(p => ({ ...p, Name: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth type="number" label="Maks. ditë/vit" value={editItem.MaxDaysYear}
                  onChange={e => setEditItem(p => ({ ...p, MaxDaysYear: +e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth type="number" label="Ditë barten (carry-over)" value={editItem.CarryOverDays}
                  onChange={e => setEditItem(p => ({ ...p, CarryOverDays: +e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Ref. ligjore" value={editItem.LpReference || ''}
                  onChange={e => setEditItem(p => ({ ...p, LpReference: e.target.value }))} helperText="p.sh. Neni 63 LP" />
              </Grid>
              <Grid item xs={6}>
                <Stack spacing={1}>
                  <FormControlLabel control={<Switch size="small" checked={editItem.IsPaid} onChange={e => setEditItem(p => ({ ...p, IsPaid: e.target.checked }))} />} label="I paguar" />
                  <FormControlLabel control={<Switch size="small" checked={editItem.RequiresCert} onChange={e => setEditItem(p => ({ ...p, RequiresCert: e.target.checked }))} />} label="Kërkon certifikatë" />
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Anulo</Button>
          <Button variant="contained" onClick={saveEdit}>Ruaj</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Fshi llojin</DialogTitle>
        <DialogContent><Typography>Ky lloj pushimi do të fshihet. Kjo veprim nuk kthehet mbrapsht.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Anulo</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Fshi</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─── Tab 4: Dokumentet ──────────────────────────────────────────────────────
function DokumentetTab() {
  const [docs, setDocs] = useState([]);
  const [editDoc, setEditDoc] = useState(null);
  const [snack, setSnack] = useState('');

  const load = useCallback(() => {
    apiService().get('documentSettings').then(r => { if (r.success) setDocs(r.data); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveDoc = async () => {
    if (!editDoc) return;
    if (editDoc.Id === 0) {
      await apiService().post('documentSettings', { ...editDoc, Id: undefined });
    } else {
      await apiService().put('documentSettings', editDoc);
    }
    setEditDoc(null);
    load();
    setSnack('Dokumenti u ruajt.');
  };

  const CATS = { identity: 'Identitet', legal: 'Juridik', health: 'Shëndet', education: 'Arsim', hr: 'HR', financial: 'Financiar' };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Konfiguro dokumentet e detyrueshme dhe opsionale për çdo punëtor. Sistemi do të kujdesë për skadimin dhe reminderat.
      </Alert>
      <SectionCard title="Kërkesat e dokumenteve" icon={<FolderOpenIcon sx={{ fontSize: 18, color: '#D97706' }} />}
        action={
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => setEditDoc({ Id: 0, Code: '', Name: '', Required: true, Category: 'hr', ReminderDays: [30, 15], AcceptedFormats: ['pdf'] })}
            sx={{ textTransform: 'none' }}>
            Shto dokument
          </Button>
        }
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              {['Kodi','Emri','Kategoria','I detyrueshëm','Reminder (ditë para)','Formatet',''].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map(doc => (
              <TableRow hover key={doc.Id}>
                <TableCell><Chip label={doc.Code} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} /></TableCell>
                <TableCell><Typography variant="body2" fontWeight={600}>{doc.Name}</Typography></TableCell>
                <TableCell><Chip size="small" label={CATS[doc.Category] || doc.Category} variant="outlined" /></TableCell>
                <TableCell>
                  <Chip size="small" label={doc.Required ? 'Detyrim' : 'Opsional'} color={doc.Required ? 'error' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {(doc.ReminderDays || []).length === 0
                      ? <Typography variant="caption" color="text.disabled">—</Typography>
                      : (doc.ReminderDays || []).map(d => <Chip key={d} label={`${d}d`} size="small" />)}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {(doc.AcceptedFormats || []).map(f => <Chip key={f} label={`.${f}`} size="small" variant="outlined" />)}
                  </Stack>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setEditDoc({ ...doc, ReminderDays: [...(doc.ReminderDays || [])] })}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      <Dialog open={!!editDoc} onClose={() => setEditDoc(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editDoc?.Id === 0 ? 'Shto dokument' : 'Edito dokumentin'}</DialogTitle>
        <DialogContent>
          {editDoc && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Kodi" value={editDoc.Code}
                  onChange={e => setEditDoc(p => ({ ...p, Code: e.target.value }))} helperText="p.sh. id_card" />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Emri" value={editDoc.Name}
                  onChange={e => setEditDoc(p => ({ ...p, Name: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Kategoria</InputLabel>
                  <Select label="Kategoria" value={editDoc.Category} onChange={e => setEditDoc(p => ({ ...p, Category: e.target.value }))}>
                    {Object.entries(CATS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth label="Formatet (me presje)" value={(editDoc.AcceptedFormats || []).join(',')}
                  onChange={e => setEditDoc(p => ({ ...p, AcceptedFormats: e.target.value.split(',').map(x => x.trim()).filter(Boolean) }))}
                  helperText="p.sh. pdf,jpg,png" />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Reminderat (ditë para skadimit)" value={(editDoc.ReminderDays || []).join(',')}
                  onChange={e => setEditDoc(p => ({ ...p, ReminderDays: e.target.value.split(',').map(x => +x.trim()).filter(n => n > 0) }))}
                  helperText="p.sh. 60,30,15 → do të dërgojë njoftim 60, 30 dhe 15 ditë para skadimit" />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={editDoc.Required} onChange={e => setEditDoc(p => ({ ...p, Required: e.target.checked }))} />}
                  label="Dokument i detyrueshëm" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDoc(null)}>Anulo</Button>
          <Button variant="contained" onClick={saveDoc}>Ruaj</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─── Tab 5: Compliance ──────────────────────────────────────────────────────
function ComplianceTab() {
  const [cfg, setCfg] = useState(null);
  const [snack, setSnack] = useState('');

  useEffect(() => {
    apiService().get('complianceSettings').then(r => { if (r.success && r.data?.length) setCfg(r.data[0]); });
  }, []);

  const save = async () => {
    await apiService().put('complianceSettings', { ...cfg, UpdatedAt: new Date().toISOString() });
    setSnack('Compliance settings u ruajt!');
  };

  if (!cfg) return <LinearProgress />;

  const totalWeight = (cfg.Rules || []).reduce((s, r) => s + r.weight, 0);

  return (
    <Box>
      <Alert severity={totalWeight !== 100 ? 'warning' : 'success'} sx={{ mb: 2 }}>
        Totali i peshave: <strong>{totalWeight}/100</strong>
        {totalWeight !== 100 && ' — Peshat duhet të jenë saktësisht 100!'}
      </Alert>

      <SectionCard title="Rregullat & peshat e compliance" icon={<VerifiedUserIcon sx={{ fontSize: 18, color: '#3E8FA3' }} />}
        action={<Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={save} sx={{ textTransform: 'none' }}>Ruaj</Button>}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              {['Rregulli','Pesha (%)','Kritik','I lartë','Mesatar',''].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(cfg.Rules || []).map((rule, i) => (
              <TableRow hover key={rule.id}>
                <TableCell><Typography variant="body2" fontWeight={600}>{rule.name}</Typography></TableCell>
                <TableCell sx={{ width: 100 }}>
                  <TextField type="number" size="small" value={rule.weight}
                    onChange={e => setCfg(p => ({ ...p, Rules: p.Rules.map((r, j) => j === i ? { ...r, weight: +e.target.value } : r) }))}
                    inputProps={{ min: 0, max: 100 }} sx={{ width: 70 }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                </TableCell>
                {['critical','high','medium'].map(level => (
                  <TableCell key={level} sx={{ width: 90 }}>
                    <TextField type="number" size="small" value={rule.threshold?.[level] ?? 0}
                      onChange={e => setCfg(p => ({ ...p, Rules: p.Rules.map((r, j) => j === i ? { ...r, threshold: { ...r.threshold, [level]: +e.target.value } } : r) }))}
                      sx={{ width: 70 }} />
                  </TableCell>
                ))}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ flex: 1, height: 6, bgcolor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${rule.weight}%`, bgcolor: '#3E8FA3', borderRadius: 3 }} />
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      <SectionCard title="Nivelet e riskut" icon={<VerifiedUserIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}>
        <Grid container spacing={1.5}>
          {(cfg.Levels || []).map(level => (
            <Grid item xs={6} sm={3} key={level.id}>
              <Box sx={{ p: 1.5, borderRadius: 1.5, border: `2px solid ${level.color}`, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: level.color, fontWeight: 800, display: 'block' }}>{level.label}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>{level.minScore}–{level.maxScore} pikë</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel control={
            <Switch checked={cfg.AutoRecalculate} onChange={e => setCfg(p => ({ ...p, AutoRecalculate: e.target.checked }))} />
          } label="Ri-kalkulo automatikisht" />
          {cfg.AutoRecalculate && (
            <TextField size="small" type="number" label="Intervali (orë)" value={cfg.RecalcIntervalHours}
              onChange={e => setCfg(p => ({ ...p, RecalcIntervalHours: +e.target.value }))}
              sx={{ width: 150 }} InputProps={{ endAdornment: <InputAdornment position="end">h</InputAdornment> }} />
          )}
        </Stack>
      </SectionCard>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ─── Tab 6: Siguria (RBAC) ──────────────────────────────────────────────────
const ROLES = [
  { id: 'admin',   name: 'Administrator', color: '#DC2626', desc: 'Qasje totale: konfigurim, fshirje, export, RBAC' },
  { id: 'hr',      name: 'HR Manager',    color: '#D97706', desc: 'Punëtorë, kontratat, pushimet, pagat, trajnimet' },
  { id: 'payroll', name: 'Payroll',       color: '#3E8FA3', desc: 'Vetëm pagat, eksport ATK/KBPK' },
  { id: 'manager', name: 'Menaxher',      color: '#0891B2', desc: 'Shikon ekipin e vet, aprovat pushimet & orët shtesë' },
  { id: 'employee',name: 'Punëtor',       color: '#3E8FA3', desc: 'Vetëm të dhënat e veta: slip paga, pushime, orari' },
];

const PERMISSIONS = [
  { section: 'Punëtorët',  admin: true,  hr: true,  payroll: false, manager: 'read', employee: 'self' },
  { section: 'Kontratat',  admin: true,  hr: true,  payroll: false, manager: 'read', employee: 'self' },
  { section: 'Pagat',      admin: true,  hr: true,  payroll: true,  manager: false,  employee: 'slip' },
  { section: 'Pushimet',   admin: true,  hr: true,  payroll: false, manager: 'approve', employee: 'request' },
  { section: 'Prezenca',   admin: true,  hr: true,  payroll: false, manager: 'team', employee: 'self' },
  { section: 'Trajnimet',  admin: true,  hr: true,  payroll: false, manager: 'read', employee: 'read' },
  { section: 'Raportet',   admin: true,  hr: true,  payroll: 'limited', manager: 'team', employee: false },
  { section: 'Compliance', admin: true,  hr: true,  payroll: false, manager: false,  employee: false },
  { section: 'Konfigurim', admin: true,  hr: false, payroll: false, manager: false,  employee: false },
  { section: 'Audit Log',  admin: true,  hr: 'read',payroll: false, manager: false,  employee: false },
];

function PermCell({ val }) {
  if (val === true) return <CheckCircleIcon sx={{ fontSize: 18, color: '#3E8FA3' }} />;
  if (val === false) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return <Chip size="small" label={val} sx={{ fontSize: 10, height: 18 }} />;
}

function SiguriTab() {
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Rolet dhe privilegjet menaxhohen edhe te <strong>Company → Roles</strong>. Kjo tabelë tregon një rezyme të qasjes sipas rolit.
      </Alert>

      <SectionCard title="Rolet & privilegjet (RBAC)" icon={<SecurityIcon sx={{ fontSize: 18, color: '#3E8FA3' }} />}
        action={
          <Button size="small" component={Link} to="/company" endIcon={<ArrowForwardIcon />} sx={{ textTransform: 'none' }}>
            Menaxho rolet
          </Button>
        }
      >
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {ROLES.map(r => (
            <Grid item xs={12} sm={4} md key={r.id}>
              <Box sx={{ p: 1.5, borderRadius: 1.5, border: `1.5px solid ${r.color}20`, bgcolor: `${r.color}08` }}>
                <Typography variant="caption" sx={{ color: r.color, fontWeight: 800, display: 'block' }}>{r.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{r.desc}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Seksioni</TableCell>
                {ROLES.map(r => (
                  <TableCell key={r.id} align="center" sx={{ fontWeight: 700, color: r.color, fontSize: 12 }}>{r.name}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {PERMISSIONS.map(p => (
                <TableRow hover key={p.section}>
                  <TableCell><Typography variant="body2" fontWeight={600}>{p.section}</Typography></TableCell>
                  {ROLES.map(r => (
                    <TableCell key={r.id} align="center"><PermCell val={p[r.id]} /></TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </SectionCard>

      <SectionCard title="Informacion GDPR" icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />}>
        <Grid container spacing={1.5}>
          {[
            { title: 'Minimizimi i të dhënave', desc: 'Çdo rol sheh vetëm atë që i nevojitet për funksionin e tij.' },
            { title: 'Audit trail', desc: 'Çdo veprim regjistrohet në Audit Log me user, IP dhe timestamp.' },
            { title: 'Pseudonimizimi', desc: 'Nr. bankar shfaqet i maskuar (****1234) tek rolet pa qasje financiare.' },
            { title: 'E drejta e fshirjes', desc: 'Administratori mund të anonimizojë të dhënat pas largimit të punëtorit.' },
          ].map(item => (
            <Grid item xs={12} sm={6} key={item.title}>
              <Box sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 1.5, border: '1px solid', borderColor: 'info.200' }}>
                <Typography variant="caption" fontWeight={700} color="info.dark" sx={{ display: 'block' }}>{item.title}</Typography>
                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </SectionCard>
    </Box>
  );
}

// ─── Tab 7: Modulet ─────────────────────────────────────────────────────────
const MODULE_LIST = [
  {
    key: 'timeTracking',
    label: 'Time Tracking',
    desc: 'Mundëson regjistrimin e kohës së punës sipas projekteve dhe detyrave. Shfaqet si seksion i veçantë në shiritin anësor.',
    // Marka, si çdo ikonë tjetër seksioni te kjo faqe — vjollca ishte e vetmja
    // që dallonte, pa asnjë kuptim që s'e thoshte etiketa.
    icon: <TimerIcon sx={{ fontSize: 20, color: '#3E8FA3' }} />,
    color: '#3E8FA3',
    badge: 'I RI',
  },
];

function ModuletTab() {
  const { modules, setModule } = useModuleSettings();
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Aktivizo ose çaktivizo module shtesë të sistemit. Ndryshimet hyjnë në fuqi menjëherë — faqja nuk ka nevojë të rifresohet.
      </Alert>
      <SectionCard
        title="Modulet e disponueshme"
        icon={<ExtensionIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
      >
        <Stack spacing={0}>
          {MODULE_LIST.map((mod, i) => (
            <React.Fragment key={mod.key}>
              {i > 0 && <Divider />}
              <Box sx={{ py: 2, px: 0.5 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: `${mod.color}12`, flexShrink: 0, mt: 0.25 }}>
                    {mod.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>{mod.label}</Typography>
                      {mod.badge && (
                        <Chip size="small" label={mod.badge}
                          sx={{ fontSize: '0.65rem', height: 18, bgcolor: `${mod.color}18`, color: mod.color, fontWeight: 700 }} />
                      )}
                      <Chip
                        size="small"
                        label={modules[mod.key] ? 'Aktiv' : 'Joaktiv'}
                        color={modules[mod.key] ? 'success' : 'default'}
                        variant={modules[mod.key] ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{mod.desc}</Typography>
                  </Box>
                  <FormControlLabel
                    sx={{ mr: 0, flexShrink: 0 }}
                    control={
                      <Switch
                        checked={!!modules[mod.key]}
                        onChange={(_, v) => setModule(mod.key, v)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Stack>
              </Box>
            </React.Fragment>
          ))}
        </Stack>
      </SectionCard>
    </Box>
  );
}

// ─── Tab 8: Ndërprerja ─────────────────────────────────────────────────────
export function NderprerjaTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [editItem, setEditItem] = useState(null);   // { id, label, sort_order, active }
  const [newLabel, setNewLabel] = useState('');
  const [addOpen, setAddOpen]   = useState(false);
  const [snack, setSnack]       = useState('');

  const API_URL = process.env.REACT_APP_API_URL || '';
  const getToken = getAuthToken;
  const headers  = () => ({ 'Content-Type':'application/json', ...(getToken() ? { Authorization:`Bearer ${getToken()}` } : {}) });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/termination-templates`, { headers: headers() }).then(x=>x.json());
      if (r?.success) setTemplates(r.data || []);
    } catch (_) {}
    setLoading(false);
  }, [API_URL]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/termination-templates`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ label: newLabel.trim(), sort_order: templates.length }),
      }).then(x=>x.json());
      if (r?.success) { setNewLabel(''); setAddOpen(false); load(); setSnack('Shablloni u shtua.'); }
      else setSnack(r?.error || 'Gabim');
    } catch (_) { setSnack('Gabim'); }
    setSaving(false);
  };

  const handleSaveEdit = async () => {
    if (!editItem?.label?.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/termination-templates/${editItem.id}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ label: editItem.label.trim(), sort_order: editItem.sort_order, active: editItem.active }),
      }).then(x=>x.json());
      if (r?.success) { setEditItem(null); load(); setSnack('Ndryshimet u ruajtën.'); }
      else setSnack(r?.error || 'Gabim');
    } catch (_) { setSnack('Gabim'); }
    setSaving(false);
  };

  const [confirm, confirmUI] = useConfirm();


  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Fshi shabllonin', message: 'Fshi shabllonin?', confirmLabel: 'Fshi' }))) return;
    try {
      const r = await fetch(`${API_URL}/api/termination-templates/${id}`, { method:'DELETE', headers: headers() }).then(x=>x.json());
      if (r?.success) { load(); setSnack('Shablloni u fshi.'); }
      else setSnack(r?.error || 'Gabim');
    } catch (_) { setSnack('Gabim'); }
  };

  const toggleActive = async (tpl) => {
    try {
      await fetch(`${API_URL}/api/termination-templates/${tpl.id}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ label: tpl.label, sort_order: tpl.sort_order, active: !tpl.active }),
      }).then(x=>x.json());
      load();
    } catch (_) {}
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Shabllone e arsyeve të ndërprerjes shfaqen si butona të shpejtë kur ndërrpret kontratën e një punëtori.
        Mund t'i shtosh, ndërrosh ose fshish sipas nevojave të kompanisë.
      </Alert>
      <SectionCard
        title="Shabllone të arsyeve"
        icon={<BlockIcon sx={{ fontSize: 18, color: 'error.main' }} />}
        action={
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => { setNewLabel(''); setAddOpen(true); }}
            sx={{ textTransform: 'none', background: 'linear-gradient(135deg,#dc2626,#b91c1c)', boxShadow:'none', borderRadius:1.5 }}
          >
            Shto shabllon
          </Button>
        }
      >
        {loading ? (
          <Box sx={{ display:'flex', justifyContent:'center', py:2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight:700 }}>Etiketa</TableCell>
                <TableCell sx={{ fontWeight:700, width:80, textAlign:'center' }}>Aktiv</TableCell>
                <TableCell sx={{ width:100 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((tpl) => (
                <TableRow key={tpl.id} hover>
                  <TableCell>
                    <Typography variant="body2">{tpl.label}</Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign:'center' }}>
                    <Switch size="small" checked={tpl.active !== false} onChange={() => toggleActive(tpl)} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Ndrysho">
                        <IconButton size="small" onClick={() => setEditItem({ ...tpl })}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Fshi">
                        <IconButton size="small" color="error" onClick={() => handleDelete(tpl.id)}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!templates.length && (
                <TableRow><TableCell colSpan={3}><Typography variant="caption" color="text.secondary">Asnjë shabllon i gjetur.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      {/* Dialog: Shto */}
      {confirmUI}

      <Dialog open={addOpen} onClose={() => !saving && setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Shto shabllon të ri</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <TextField
            label="Etiketa e shabllonit"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            fullWidth size="small"
            placeholder="p.sh. Skadim kontrate"
            onKeyDown={e => e.key==='Enter' && handleAdd()}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Anulo</Button>
          <Button onClick={handleAdd} variant="contained" color="error" disabled={saving || !newLabel.trim()}
            startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <AddIcon />}>
            {saving ? 'Duke shtuar…' : 'Shto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Ndrysho */}
      <Dialog open={Boolean(editItem)} onClose={() => !saving && setEditItem(null)} fullWidth maxWidth="xs">
        <DialogTitle>Ndrysho shabllonin</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <TextField
            label="Etiketa"
            value={editItem?.label || ''}
            onChange={e => setEditItem(p => ({ ...p, label: e.target.value }))}
            fullWidth size="small"
            autoFocus
            onKeyDown={e => e.key==='Enter' && handleSaveEdit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)} disabled={saving}>Anulo</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={saving || !editItem?.label?.trim()}
            startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon />}>
            {saving ? 'Duke ruajtur…' : 'Ruaj'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical:'bottom', horizontal:'center' }} />
    </Box>
  );
}

// ─── Main Settings Page ──────────────────────────────────────────────────────
const TABS = [
  { label: 'Fiskal', icon: <AccountBalanceIcon sx={{ fontSize: 16 }} /> },
  { label: 'Orari',  icon: <ScheduleIcon sx={{ fontSize: 16 }} /> },
  { label: 'Pushimet', icon: <BeachAccessIcon sx={{ fontSize: 16 }} /> },
  { label: 'Dokumentet', icon: <FolderOpenIcon sx={{ fontSize: 16 }} /> },
  { label: 'Ndërprerja', icon: <BlockIcon sx={{ fontSize: 16 }} /> },
  { label: 'Llojet e punës', icon: <ExtensionIcon sx={{ fontSize: 16 }} /> },
];

/** Indeksi i tab-it të llojeve — lidhjet nga jashtë e përdorin këtë, jo një numër të shkruar. */
export const WORKING_TYPES_TAB = TABS.findIndex((t) => t.label === 'Llojet e punës');

export default function SettingsPage() {
  const location = useLocation();
  const history  = useHistory();

  // Tab-i aktiv vjen nga URL (?tab=), si te UserManagementPage: kështu një
  // lidhje mund ta hapë drejt «Llojet e punës» — p.sh. nga zgjedhësi te forma e
  // orarit, ku njeriu sapo kuptoi se i mungon një lloj. Pa këtë, faqja hapej
  // gjithmonë te tab-i i parë dhe deep-link/back nuk kishin kuptim.
  const tabFromUrl = parseInt(new URLSearchParams(location.search).get('tab'), 10);
  // Rruga /work-types hapet te llojet edhe pa `?tab=`, që lidhja nga zgjedhësi
  // të mos mbajë një indeks të shkruar me dorë — indeksat zhvendosen kur shtohet
  // një tab, dhe atëherë lidhja do të çonte diku tjetër pa e vënë kush re.
  const fallback = location.pathname.includes('work-types') ? WORKING_TYPES_TAB : 0;
  const tab    = TABS[tabFromUrl] ? tabFromUrl : fallback;
  const setTab = (v) => history.replace(`${location.pathname}${v ? `?tab=${v}` : ''}`);

  return (
    <Box>
      <PageHeader
        title="Konfigurim"
        subtitle="Menaxho politikat fiskale, orarin, pushimet, dokumentet dhe modulet"
      />
      <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', gap: 0.75 },
            '& .Mui-selected': { color: 'primary.main' },
          }}
        >
          {TABS.map((t, i) => (
            <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />
          ))}
        </Tabs>
        <Box sx={{ p: 2 }}>
          <TabPanel value={tab} index={0}><FiskalTab /></TabPanel>
          <TabPanel value={tab} index={1}><OrariTab /></TabPanel>
          <TabPanel value={tab} index={2}><PushimetTab /></TabPanel>
          <TabPanel value={tab} index={3}><DokumentetTab /></TabPanel>
          <TabPanel value={tab} index={4}><NderprerjaTab /></TabPanel>
          <TabPanel value={tab} index={5}><WorkingTypesTab /></TabPanel>
        </Box>
      </Paper>
    </Box>
  );
}
